import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import '../styles.css';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to view your profile.');
        navigate('/login');
        return;
      }

      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/user/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = res.data;
        setUser(data);
        setFullName(data.full_name || '');
        setEmail(data.email || '');
        setDateOfBirth(data.date_of_birth ? data.date_of_birth.split('T')[0] : '');
        setWalletAddress(data.wallet_address || '');
      } catch (err) {
        console.error('Profile fetch error:', err.response?.data || err.message);
        setError(err.response?.data?.error || 'Failed to load profile');
        setUser(null); // Ensure user is null on error
        if (err.response?.status === 401) {
          localStorage.removeItem('token'); // Clear invalid token
          navigate('/login');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Please log in to update your profile.');
      setLoading(false);
      navigate('/login');
      return;
    }

    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}/api/user/profile`,
        {
          full_name: fullName,
          date_of_birth: dateOfBirth,
          wallet_address: walletAddress,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMessage(res.data.message || 'Profile updated successfully!');
      setUser({ ...user, full_name: fullName, date_of_birth: dateOfBirth, wallet_address: walletAddress });
    } catch (err) {
      console.error('Profile update error:', err.response?.data || err.message);
      setError(err.response?.data?.error || 'Failed to update profile');
      if (err.response?.status === 401) {
        localStorage.removeItem('token'); // Clear invalid token
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <Header />
        <section className="content-container container my-4 text-center">
          <p>Loading profile...</p>
        </section>
        <Footer />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="page-container">
        <Header />
        <section className="content-container container my-4 text-center">
          <p className="text-danger">{error || 'Profile data not available'}</p>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/login')}>Go to Login</button>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="page-container">
      <Header />
      <section className="content-container container my-4">
        <h1 className="text-center mb-4 text-white">My Profile</h1>
        {error && <p className="text-danger text-center">{error}</p>}
        {successMessage && <p className="text-success text-center">{successMessage}</p>}
        <div className="card p-4 mx-auto card-mid-width">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label htmlFor="userId" className="form-label">Your User ID:</label>
              <input
                type="text"
                id="userId"
                className="form-control"
                value={user.user_id || 'N/A'}
                disabled
              />
            </div>
            <div className="mb-3">
              <label htmlFor="fullName" className="form-label">Full Name:</label>
              <input
                type="text"
                id="fullName"
                className="form-control"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="email" className="form-label">Email:</label>
              <input
                type="email"
                id="email"
                className="form-control"
                value={email}
                disabled
              />
            </div>
            <div className="mb-3">
              <label htmlFor="dateOfBirth" className="form-label">Date of Birth:</label>
              <input
                type="date"
                id="dateOfBirth"
                className="form-control"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label htmlFor="walletAddress" className="form-label">Your Receiving Wallet Address:</label>
              <input
                type="text"
                id="walletAddress"
                className="form-control text-success"
                value={walletAddress || 'Please Add Your Wallet Address, if not You Will Not Receive Tokens'}
                disabled
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Referral Code</label>
              <input
                type="text"
                className="form-control"
                value={user.referral_code || 'N/A'}
                disabled
              />
            </div>
            <button type="submit" className="btn btn-primary w-100" disabled={loading}>
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Profile;
